/**
 * Smooth Sales "Notify Me" for sold-out product cards
 * (snippets/notify-me-card.liquid).
 *
 * A delegated click on any .notify-me-card__trigger opens a shared
 * email-capture <dialog> (built once, lazily) and subscribes the email
 * to Klaviyo's back-in-stock flow for the card's variant — the same
 * public client API the PDP notify block uses (assets/back-in-stock.js).
 */

const ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const REVISION = '2024-10-15';

let dialog = null;
let lastTrigger = null;

function ensureDialog() {
  if (dialog) return dialog;
  dialog = document.createElement('dialog');
  dialog.className = 'ss-notify-modal';
  dialog.innerHTML = `
    <div class="ss-notify-modal__inner">
      <button type="button" class="ss-notify-modal__close" aria-label="Close">&times;</button>
      <h2 class="ss-notify-modal__heading">Get notified when it's back</h2>
      <p class="ss-notify-modal__product" data-product-title></p>
      <form class="ss-notify-modal__form" novalidate>
        <input
          class="ss-notify-modal__input"
          type="email"
          name="email"
          required
          autocomplete="email"
          placeholder="Your email address"
          aria-label="Email address"
        >
        <button type="submit" class="ss-notify-modal__submit">Notify Me</button>
      </form>
      <p class="ss-notify-modal__status" hidden></p>
    </div>
  `;

  dialog.querySelector('.ss-notify-modal__close').addEventListener('click', () => dialog.close());

  // Click on the backdrop (the dialog element itself, outside the inner
  // panel) closes the modal.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  dialog.addEventListener('close', () => {
    lastTrigger?.focus();
    lastTrigger = null;
  });

  dialog.querySelector('.ss-notify-modal__form').addEventListener('submit', onSubmit);

  document.body.append(dialog);
  return dialog;
}

function openModal(trigger) {
  const modal = ensureDialog();
  modal.dataset.variantId = trigger.dataset.variantId || '';
  modal.dataset.publicKey = trigger.dataset.publicKey || '';
  modal.removeAttribute('data-done');

  modal.querySelector('[data-product-title]').textContent = trigger.dataset.productTitle || '';

  const status = modal.querySelector('.ss-notify-modal__status');
  status.hidden = true;
  status.removeAttribute('data-state');
  status.textContent = '';

  const input = modal.querySelector('.ss-notify-modal__input');
  input.value = trigger.dataset.email || '';
  modal.querySelector('.ss-notify-modal__submit').disabled = false;

  lastTrigger = trigger;
  modal.showModal();
  input.focus();
}

async function onSubmit(event) {
  event.preventDefault();
  const form = event.currentTarget;
  const input = form.querySelector('.ss-notify-modal__input');
  const email = (input.value || '').trim();
  if (email === '' || !input.checkValidity()) {
    input.reportValidity();
    return;
  }

  const submit = form.querySelector('.ss-notify-modal__submit');
  const status = dialog.querySelector('.ss-notify-modal__status');
  submit.disabled = true;

  let ok = false;
  try {
    const response = await fetch(`${ENDPOINT}?company_id=${encodeURIComponent(dialog.dataset.publicKey || '')}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${dialog.dataset.variantId || ''}`,
              },
            },
          },
        },
      }),
    });
    ok = response.ok;
  } catch (error) {
    ok = false;
  }

  status.hidden = false;
  status.dataset.state = ok ? 'ok' : 'error';
  status.textContent = ok
    ? "You're on the list — we'll email you the moment it's back."
    : "Sorry, that didn't go through. Please try again.";

  if (ok) {
    dialog.dataset.done = 'true';
  } else {
    submit.disabled = false;
  }
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('.notify-me-card__trigger');
  if (!trigger) return;
  event.preventDefault();
  openModal(trigger);
});
